import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Form, Modal, Spinner } from "react-bootstrap";
import { FaCopy, FaPaste, FaPlus, FaSave, FaTrash } from "react-icons/fa";
import Select from "react-select";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import axiosInstance from "../../axiosInstance";
import AdLayout from "../../components/AdLayout";
import { fetchAccountingPermissions, ACCOUNTING_CONFIG_SALARY } from "../../libs/customerCreatePermission";
import { groupMoney, unformatMoney } from "./utils/moneyFormat";
import "./accountingResponsive.css";

const unwrap = (res) => res?.data?.data ?? res?.data ?? null;

const SALARY_TYPES = [
  { value: "OFFICIAL", label: "Chính thức" },
  { value: "SERVICE", label: "Cộng tác viên / Dịch vụ" },
];

// Các cột nhập số trong cấu hình lương.
const CFG_FIELDS = [
  { key: "standardWorkDays", label: "Ngày công chuẩn", sm: true, int: true },
  { key: "baseInsurance", label: "Lương CB (đóng BH)" },
  { key: "baseSalary", label: "Lương cơ bản" },
  { key: "actualWorkDays", label: "Công thực tế", sm: true },
  { key: "holidayDays", label: "Nghỉ lễ 100%", sm: true },
  { key: "allowance", label: "Phụ cấp" },
  { key: "overtimeHours", label: "Giờ làm thêm", sm: true },
  { key: "revenue", label: "Doanh số" },
  { key: "revenueSalary", label: "Lương theo DS" },
  { key: "bonus", label: "Thưởng DS/TN" },
  { key: "personalDeduction", label: "Giảm trừ bản thân" },
  { key: "dependentDeduction", label: "Giảm trừ gia cảnh" },
  { key: "advance", label: "Tạm ứng" },
];

const emptyDraft = () => ({
  position: "",
  salaryType: "OFFICIAL",
  standardWorkDays: 26,
  baseInsurance: 0,
  baseSalary: 0,
  actualWorkDays: "",
  holidayDays: 0,
  allowance: 0,
  overtimeHours: 0,
  revenue: 0,
  revenueSalary: 0,
  bonus: 0,
  personalDeduction: 0,
  dependentDeduction: 0,
  advance: 0,
  lineIds: [],
});

const draftFromConfig = (cfg) => {
  const base = emptyDraft();
  if (!cfg) return base;
  Object.keys(base).forEach((k) => {
    if (cfg[k] !== null && cfg[k] !== undefined) base[k] = cfg[k];
  });
  if (Array.isArray(cfg.lineIds)) {
    base.lineIds = cfg.lineIds.map(String);
  } else {
    base.lineIds = [];
  }
  return base;
};

const SalaryConfigList = ({ embedded = false }) => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [lineOptions, setLineOptions] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [search, setSearch] = useState("");

  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ manualName: "", position: "", salaryType: "OFFICIAL" });
  const [adding, setAdding] = useState(false);
  const [clipboard, setClipboard] = useState(null);
  const [perm, setPerm] = useState({ canCreate: false, canEdit: false, canDelete: false });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const p = await fetchAccountingPermissions(axiosInstance, ACCOUNTING_CONFIG_SALARY);
      if (!cancelled) {
        setPerm({
          canCreate: Boolean(p?.canCreate),
          canEdit: Boolean(p?.canEdit),
          canDelete: Boolean(p?.canDelete),
        });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const resolveDeptLabel = useCallback(
    (user) => {
      const divIds = Array.isArray(user.divisionIds) && user.divisionIds.length
        ? user.divisionIds.map((x) => Number(x))
        : user.divisionId != null && user.divisionId !== ""
          ? [Number(user.divisionId)]
          : [];
      const labels = [];
      divIds.forEach((divId) => {
        for (const d of departments) {
          const div = (d.divisions || []).find((x) => Number(x.id) === Number(divId));
          if (div) {
            const dn = (d.name || "").trim();
            const dvn = (div.name || "").trim();
            labels.push(dn && dvn ? `${dn} > ${dvn}` : dn || dvn);
            break;
          }
        }
      });
      return labels;
    },
    [departments]
  );

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, deptRes, cfgRes, lineRes] = await Promise.all([
        axiosInstance.get("/api/users/searchAdminUsers", { params: { page: 0, size: 1000 } }).catch(() => null),
        axiosInstance.get("/api/users/departments").catch(() => null),
        axiosInstance.get("/api/accounting/salary-configs").catch(() => null),
        axiosInstance.get("/api/accounting/payment-vouchers/lines").catch(() => null),
      ]);
      const userList = usersRes?.data?.page?.content || usersRes?.data?.content || [];
      setUsers(Array.isArray(userList) ? userList.filter((u) => u.isMember) : []);
      setDepartments(Array.isArray(deptRes?.data) ? deptRes.data : []);
      const cfgList = unwrap(cfgRes);
      setConfigs(Array.isArray(cfgList) ? cfgList : []);
      const lineData = unwrap(lineRes);
      const lines = Array.isArray(lineData) ? lineData : [];
      setLineOptions(lines.map((l) => ({ value: String(l.id), label: l.name || `Line ${l.id}` })));
    } catch (_) {
      toast.error("Không thể tải cấu hình lương");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const configByEmployee = useMemo(() => {
    const map = {};
    configs.forEach((c) => {
      if (c.employeeId != null) map[c.employeeId] = c;
    });
    return map;
  }, [configs]);

  const manualConfigs = useMemo(() => configs.filter((c) => c.employeeId == null), [configs]);

  const rows = useMemo(() => {
    const list = [];
    users.forEach((u) => {
      list.push({
        key: `u-${u.id}`,
        employeeId: u.id,
        name: u.name || u.username || u.email || "",
        isAdmin: u.isAdmin,
        isMember: u.isMember,
        deptLabels: resolveDeptLabel(u),
        config: configByEmployee[u.id] || null,
        manual: false,
      });
    });
    manualConfigs.forEach((c) => {
      list.push({
        key: `m-${c.id}`,
        employeeId: null,
        configId: c.id,
        name: c.manualName || "(Chưa đặt tên)",
        isAdmin: false,
        isMember: false,
        deptLabels: [],
        config: c,
        manual: true,
      });
    });
    const kw = search.trim().toLowerCase();
    if (!kw) return list;
    return list.filter((r) => r.name.toLowerCase().includes(kw));
  }, [users, manualConfigs, configByEmployee, resolveDeptLabel, search]);

  // Khởi tạo draft khi có dữ liệu.
  useEffect(() => {
    setDrafts((prev) => {
      const next = { ...prev };
      rows.forEach((r) => {
        if (!next[r.key]) next[r.key] = draftFromConfig(r.config);
      });
      return next;
    });
  }, [rows]);

  const setField = (key, field, value) => {
    setDrafts((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  };

  const copyRow = (key) => {
    const d = drafts[key] || emptyDraft();
    const snap = {};
    CFG_FIELDS.forEach((f) => { snap[f.key] = d[f.key]; });
    snap.lineIds = [...(d.lineIds || [])];
    setClipboard(snap);
    toast.success("Đã copy cấu hình lương");
  };

  const pasteRow = (key) => {
    if (!clipboard) {
      toast.warn("Chưa có dữ liệu để dán");
      return;
    }
    setDrafts((prev) => ({ ...prev, [key]: { ...prev[key], ...clipboard } }));
    toast.success("Đã dán cấu hình lương");
  };

  const buildPayload = (row) => {
    const d = drafts[row.key] || emptyDraft();
    const payload = { ...d };
    CFG_FIELDS.forEach((f) => {
      if (f.key === "actualWorkDays") {
        payload.actualWorkDays = d.actualWorkDays === "" || d.actualWorkDays == null ? null : Number(d.actualWorkDays);
      } else {
        payload[f.key] = d[f.key] === "" || d[f.key] == null ? 0 : Number(d[f.key]);
      }
    });
    payload.standardWorkDays = Number(d.standardWorkDays) > 0 ? Number(d.standardWorkDays) : 26;
    payload.lineIds = (d.lineIds || []).map((id) => Number(id)).filter((id) => !Number.isNaN(id));
    if (row.manual) {
      payload.id = row.configId;
      payload.employeeId = null;
      payload.manualName = row.name;
    } else {
      payload.employeeId = row.employeeId;
    }
    return payload;
  };

  const saveRow = async (row) => {
    setSavingKey(row.key);
    try {
      await axiosInstance.put("/api/accounting/salary-configs", buildPayload(row));
      toast.success(`Đã lưu cấu hình: ${row.name}`);
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || "Lưu cấu hình thất bại");
    } finally {
      setSavingKey(null);
    }
  };

  const deleteManual = async (row) => {
    const result = await Swal.fire({
      title: "Xoá nhân viên thủ công?",
      text: row.name,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Xoá",
      cancelButtonText: "Huỷ",
    });
    if (!result.isConfirmed) return;
    try {
      await axiosInstance.delete(`/api/accounting/salary-configs/${row.configId}`);
      toast.success("Đã xoá");
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || "Xoá thất bại");
    }
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!addForm.manualName.trim()) {
      toast.warn("Vui lòng nhập họ tên");
      return;
    }
    setAdding(true);
    try {
      await axiosInstance.post("/api/accounting/salary-configs/manual", {
        manualName: addForm.manualName.trim(),
        position: addForm.position.trim(),
        salaryType: addForm.salaryType,
        standardWorkDays: 26,
      });
      toast.success("Đã thêm nhân viên");
      setShowAdd(false);
      setAddForm({ manualName: "", position: "", salaryType: "OFFICIAL" });
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || "Thêm nhân viên thất bại");
    } finally {
      setAdding(false);
    }
  };

  const body = (
    <div className={embedded ? "" : "acc-page"}>
      <Card className="mb-3">
        <Card.Body className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ maxWidth: 360 }}>
            <Form.Control
              size="sm"
              placeholder="Tìm theo tên nhân viên..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {perm.canCreate && (
            <Button variant="primary" size="sm" onClick={() => setShowAdd(true)}>
              <FaPlus className="me-1" /> Thêm nhân viên
            </Button>
          )}
        </Card.Body>
      </Card>

      <Card style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : rows.length === 0 ? (
            <div className="text-center py-5 text-muted">Chưa có nhân viên</div>
          ) : (
            <div className="acc-table-wrap acc-salary-scroll">
              <table className="table table-bordered table-hover align-middle mb-0 acc-salary-table">
                <thead>
                  <tr>
                    <th className="acc-sticky-col" style={{ minWidth: 160 }}>Nhân viên</th>
                    <th style={{ minWidth: 160 }}>Phòng ban - Bộ phận</th>
                    <th style={{ minWidth: 220 }}>Line</th>
                    <th style={{ minWidth: 140 }}>Chức vụ</th>
                    <th style={{ minWidth: 150 }}>Loại lương</th>
                    {CFG_FIELDS.map((f) => (
                      <th key={f.key} className="text-end">{f.label}</th>
                    ))}
                    <th style={{ minWidth: 110 }} className="text-center">Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const d = drafts[row.key] || emptyDraft();
                    const selectedLines = lineOptions.filter((o) => (d.lineIds || []).includes(o.value));
                    return (
                      <tr key={row.key}>
                        <td className="acc-sticky-col fw-semibold">{row.name}</td>
                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            {row.deptLabels.length ? (
                              row.deptLabels.map((l, i) => (
                                <span key={i} className="badge rounded-pill bg-secondary-subtle text-dark border">{l}</span>
                              ))
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </div>
                        </td>
                        <td style={{ minWidth: 220 }}>
                          <Select
                            isMulti
                            isDisabled={!perm.canEdit}
                            options={lineOptions}
                            value={selectedLines}
                            onChange={(opts) => setField(row.key, "lineIds", (opts || []).map((o) => o.value))}
                            placeholder="Chọn line..."
                            classNamePrefix="react-select"
                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                              control: (base) => ({ ...base, minHeight: 31, fontSize: "0.8rem" }),
                              multiValue: (base) => ({
                                ...base,
                                backgroundColor: "#a9e7af",
                                borderRadius: 4,
                              }),
                              multiValueLabel: (base) => ({
                                ...base,
                                color: "#000",
                                padding: "2px 4px",
                              }),
                              multiValueRemove: (base) => ({
                                ...base,
                                color: "#000",
                                ":hover": { backgroundColor: "rgba(0,0,0,0.08)", color: "#000" },
                              }),
                            }}
                          />
                        </td>
                        <td>
                          <Form.Control
                            size="sm"
                            className="acc-salary-input"
                            value={d.position || ""}
                            disabled={!perm.canEdit}
                            onChange={(e) => setField(row.key, "position", e.target.value)}
                          />
                        </td>
                        <td>
                          <Form.Select
                            size="sm"
                            className="acc-salary-input"
                            value={d.salaryType || "OFFICIAL"}
                            disabled={!perm.canEdit}
                            onChange={(e) => setField(row.key, "salaryType", e.target.value)}
                          >
                            {SALARY_TYPES.map((t) => (
                              <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                          </Form.Select>
                        </td>
                        {CFG_FIELDS.map((f) => (
                          <td key={f.key} className="text-end">
                            {f.sm ? (
                              <Form.Control
                                type="number"
                                size="sm"
                                className="acc-salary-input acc-salary-input-sm"
                                value={d[f.key] ?? ""}
                                disabled={!perm.canEdit}
                                onChange={(e) => setField(row.key, f.key, e.target.value)}
                              />
                            ) : (
                              <Form.Control
                                type="text"
                                inputMode="numeric"
                                size="sm"
                                className="acc-salary-input"
                                value={groupMoney(d[f.key])}
                                disabled={!perm.canEdit}
                                onChange={(e) => setField(row.key, f.key, unformatMoney(e.target.value))}
                              />
                            )}
                          </td>
                        ))}
                        <td className="text-center">
                          <div className="acc-action-btns">
                            {perm.canEdit && (
                              <Button
                                variant="outline-primary"
                                size="sm"
                                title="Lưu"
                                disabled={savingKey === row.key}
                                onClick={() => saveRow(row)}
                              >
                                {savingKey === row.key ? <Spinner animation="border" size="sm" /> : <FaSave />}
                              </Button>
                            )}
                            <Button variant="outline-secondary" size="sm" title="Copy cấu hình" onClick={() => copyRow(row.key)}>
                              <FaCopy />
                            </Button>
                            {perm.canEdit && (
                              <Button variant="outline-info" size="sm" title="Dán cấu hình" disabled={!clipboard} onClick={() => pasteRow(row.key)}>
                                <FaPaste />
                              </Button>
                            )}
                            {row.manual && perm.canDelete && (
                              <Button variant="outline-danger" size="sm" title="Xoá" onClick={() => deleteManual(row)}>
                                <FaTrash />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showAdd} onHide={() => setShowAdd(false)} centered>
        <Modal.Header closeButton><Modal.Title>Thêm nhân viên</Modal.Title></Modal.Header>
        <Form onSubmit={submitAdd}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Họ tên</Form.Label>
              <Form.Control
                value={addForm.manualName}
                onChange={(e) => setAddForm((p) => ({ ...p, manualName: e.target.value }))}
                autoFocus
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Chức vụ</Form.Label>
              <Form.Control
                value={addForm.position}
                onChange={(e) => setAddForm((p) => ({ ...p, position: e.target.value }))}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Loại lương</Form.Label>
              <Form.Select
                value={addForm.salaryType}
                onChange={(e) => setAddForm((p) => ({ ...p, salaryType: e.target.value }))}
              >
                {SALARY_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAdd(false)}>Huỷ</Button>
            <Button variant="primary" type="submit" disabled={adding}>
              {adding ? <Spinner animation="border" size="sm" /> : "Thêm"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );

  if (embedded) return body;
  return <AdLayout>{body}</AdLayout>;
};

export default SalaryConfigList;
